import { MouseEventHandler, useMemo } from 'react';

import { addStyles, Spinner } from '../..';

export interface ButtonProps {
  /** Contents of the button */
  children?: React.ReactNode;
  /** This adds an icon to the left of the button contents */
  icon?: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string;
    }
  >;
  /** This determines the appearance of the button. */
  type?: 'default' | 'primary' | 'link' | 'text' | 'action' | 'inherit' | 'danger';
  /** This adds on custom css classes */
  className?: string;
  /** This overrides the styles of the component */
  style?: React.CSSProperties;
  /** This displays a loading state when set to true */
  loading?: boolean;
  /** This disables the button when set to true. */
  disabled?: boolean;
  /** Button sizes */
  size?: 'small' | 'medium' | 'large';
  /** This sets the "type" attribute of the html button. */
  htmlType?: 'button' | 'submit' | 'reset';
  /** The onClick callback for the button element. */
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /** Link url */
  href?: string;
  /** Where to open the link. (e.g. new tab) */
  target?: '_blank' | '_parent' | '_self' | '_top';
  /** The html title attribute for the <button> tag */
  title?: string;
}

const focusClasses = 'outline-none focus-visible:ring-2 focus-visible:ring-midnight-900 focus-visible:ring-offset-2';
const classes = {
  base: 'flex flex-row h-min w-fit rounded-lg items-center justify-center cursor-pointer fill-current',
  sizes: {
    large: {
      withLabel: 'py-2.5 px-5 gap-x-2',
      withoutLabel: 'p-2.5',
      icon: 'size-5 m-px',
    },
    medium: {
      withLabel: 'py-2 px-4 gap-x-2',
      withoutLabel: 'p-2',
      icon: 'size-5 m-px',
    },
    small: {
      withLabel: 'py-1 px-2 gap-x-2',
      withoutLabel: 'p-1',
      icon: 'size-5 m-px',
    },
  },
  loading: `bg-jungle-50 text-primary ${focusClasses}`,
  variants: {
    default:
      `bg-white text-black border border-gray-600 hover:bg-jungle-50 hover:text-primary ` +
      `active:text-jungle-900 active:bg-jungle-100 active:border-jungle-900 ${focusClasses} ` +
      `focus-visible:text-gray-900 disabled:bg-white disabled:text-gray-600 disabled:border-gray-600`,
    primary: `bg-primary hover:bg-jungle-800 active:bg-jungle-900 text-white ${focusClasses} disabled:bg-jungle-400`,
    link:
      `text-primary hover:bg-gray-100 hover:text-primary active:bg-gray-200 ${focusClasses} ` +
      `disabled:bg-transparent`,
    action:
      `text-primary hover:bg-jungle-200 active:bg-jungle-300 fill-jungle-900 hover:fill-gray-900 ${focusClasses} ` +
      `active:fill-gray-900 hover:text-gray-900 active:text-gray-900 border hover:border-jungle-200 ` +
      `active:border-transparent border-transparent disabled:bg-transparent disabled:border-transparent ` +
      `disabled:text-gray-600`,
    text: `text-gray-900 hover:bg-gray-100 active:bg-gray-200 ${focusClasses} disabled:bg-transparent`,
    inherit: '',
    danger: `bg-red-500 hover:bg-red-600 active:bg-red-700 text-white ${focusClasses} disabled:bg-red-400`,
  },
};

/** Basic button that has several variations.
 * It has allows for including an icon, comes in several sizes and variations,
 * has a loading with a spinner, and can be used as a link. */
export const Button = (props: ButtonProps) => {
  const {
    icon,
    children,
    className,
    style,
    type = 'default',
    size = 'large',
    loading,
    disabled,
    htmlType = 'button',
    onClick,
    href,
    target = '_blank',
    title,
  } = props;

  // Combine styling based on size / type / loading state of component as
  // well as overriding via className and style props
  const classNameMemo = useMemo(() => {
    const sizeClasses = children ? classes.sizes[size].withLabel : classes.sizes[size].withoutLabel;
    const colorClasses = loading ? classes.loading : classes.variants[type];
    return addStyles([classes.base, colorClasses, sizeClasses, className]);
  }, [type, size, children, className, loading]);

  const contents = (
    <>
      {icon && !loading && icon({ className: classes.sizes[size].icon, title: 'ButtonIcon' })}
      {loading && <Spinner size="small" />}
      {children}
    </>
  );

  return href ? (
    <a title={title} className={classNameMemo} style={style} href={href} target={target}>
      {contents}
    </a>
  ) : (
    <button title={title} className={classNameMemo} style={style} type={htmlType} disabled={disabled} onClick={onClick}>
      {contents}
    </button>
  );
};

export default Button;
